import React, { useEffect, useState } from "react";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { FiMapPin } from "react-icons/fi";
import { PiBuildingLight } from "react-icons/pi";
import { Modal, ModalBody } from "reactstrap";

import Loader from "@components/shared/Loader/Loader";
import WarningMessageBox from "@components/Unit/WarningMessageBox/WarningMessageBox";
import { PAYMENT_STATUS, USER_ROLE } from "@constants/Bookings";
import { CONSTANTS } from "@constants/index";
import { DateFormats } from "@enums/shared.enum";
import {
  BookingDetailsType,
  ReservationDetailsType,
} from "@models/components/user.model";
import handleResponse from "@services/apiConfig/api.response.service";
import { applyLocalization } from "@services/common.service";
import API_SERVICES from "@services/index";

import BookedSlot from "../BookedSlot/BookedSlot";
import DetailBox from "../DetailBox/DetailBox";
import PaymentMethod from "../PaymentMethod/PaymentMethod";
import PriceBlock from "../PriceBlock/PriceBlock";

const ReservationDetails: React.FC<ReservationDetailsType> = ({
  bookingData,
  isOpen,
  toggleCancelModal,
  toggleOpen,
}) => {
  const { t } = useTranslation();
  const { bookingId } = bookingData;
  const [bookingDetails, setBookingDetails] = useState<BookingDetailsType>(
    {} as BookingDetailsType
  );
  const [isFetching, setIsFetching] = useState(false);

  const location = {
    latitude: bookingDetails?.location?.map?.latitude,
    location: bookingDetails?.location?.mapLocation,
    longitude: bookingDetails?.location?.map?.longitude,
  };

  const bookedTimeSlot = bookingDetails?.timeSlot
    ? `${bookingDetails?.timeSlot} ${t("min")}`
    : "";

  const slotTypeValue = bookedTimeSlot;

  const isShowDeleteButton =
    bookingDetails?.role === USER_ROLE.ORGANIZER_ROLE &&
    moment(bookingDetails?.startDate)
      .startOf(DateFormats.Day)
      .isSameOrAfter(moment().startOf(DateFormats.Day)) &&
    !bookingDetails?.bookingCanceled &&
    bookingDetails?.bookingCancelable;

  const isShowWarningMsg =
    !bookingDetails?.bookingCancelable && bookingDetails?.reason;

  const isShowCancelReason = !isShowWarningMsg && isShowDeleteButton;

  useEffect(() => {
    getUserBookingDetailsFn();
  }, []);

  const getUserBookingDetailsFn = async () => {
    setIsFetching(true);
    const data = await API_SERVICES.USER.GET_USER_BOOKING_DETAILS(bookingId);
    const responseData = handleResponse(data);
    if (responseData?.ok) {
      setBookingDetails(responseData.data);
      setIsFetching(false);
    } else setIsFetching(false);
  };
  const formatAddress = (address?: string) =>
    address
      ? address.split("\n").map((line, i) => (
          <span className="mb-2 d-block" key={i}>
            {line}
          </span>
        ))
      : "";
  const getPaymentStatus = (bookingDetails: BookingDetailsType) => {
    let status = "";

    const paymentStatus = bookingDetails?.paymentStatus ?? "";

    if (paymentStatus === PAYMENT_STATUS.SUCCESS) {
      status = PAYMENT_STATUS.PAID;

      if (bookingDetails?.isPartialPayment) status = PAYMENT_STATUS.PARTPAYMENT;
    }

    return status || paymentStatus;
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggleOpen}
      data-testid="reservation-details-modal"
      id="reservation-details-modal"
    >
      <Loader isLoading={isFetching} />
      <ModalBody>
        <>
          <h5>{applyLocalization(bookingDetails?.club?.title)}</h5>
          <button
            className="fg-close-btn"
            data-testid="closeButton"
            aria-label="Close"
            onClick={toggleOpen}
          >
            <i className="fa-sharp fa-solid fa-xmark"></i>
          </button>
          <div className="mb-1">
            {t("bookedOn")}:
            <span className="ms-2">
              {moment(bookingDetails?.startDate).format(
                CONSTANTS.DATE_FORMATS.DAY_MONTH_YEAR_FORMAT
              )}
            </span>
          </div>
          {bookingDetails?.passcode != null && (
            <div>
              {t("passcode")}:
              <span className="ms-2">{bookingDetails.passcode}</span>
            </div>
          )}
          <hr className="mt-3 mb-3" />
          <div className="d-flex">
            <div>
              <div className="d-flex gap-2 mb-2">
                <h6>
                  <FiMapPin className="me-2 mb-1" />
                  {applyLocalization(bookingDetails?.location?.title)}
                  <DetailBox
                    label=""
                    value={formatAddress(
                      applyLocalization(bookingDetails?.location?.address)
                    )}
                    location={location}
                  />
                </h6>
              </div>
              <div className="d-flex gap-2">
                <PiBuildingLight />
                <h6>{applyLocalization(bookingDetails?.resource?.title)}</h6>
              </div>

              {bookingDetails?.guests > 0 && (
                <div>
                  {" "}
                  {t("guestCount")}: {bookingDetails?.guests}
                </div>
              )}
            </div>
            <div className="ms-auto flex-shrink-0">
              {bookingDetails?.startTime &&
                bookingDetails?.endTime &&
                slotTypeValue && (
                  <BookedSlot
                    showHeading={false}
                    startTime={bookingDetails?.startTime || ""}
                    endTime={bookingDetails?.endTime || ""}
                    slotType={slotTypeValue}
                  />
                )}
            </div>
          </div>
          <div className="bg-light p-2">
            {(bookingDetails?.paymentStatus != null ||
              (bookingDetails?.payment?.type &&
                bookingDetails?.payment?.type !== "NONE")) && (
              <div className="d-flex gap-3 mb-3">
                {bookingDetails?.paymentStatus != null && (
                  <div className="payment-status-wrapper">
                    <PaymentMethod
                      isStatus
                      label=""
                      value={getPaymentStatus(bookingDetails)}
                    />
                  </div>
                )}
                {bookingDetails?.payment?.type &&
                  bookingDetails?.payment?.type !== "NONE" && (
                    <div className="payment-type-wrapper">
                      <PaymentMethod
                        label=""
                        value={bookingDetails?.payment?.type}
                      />
                    </div>
                  )}
              </div>
            )}
            <div className="price-block mt-2">
              <PriceBlock bookingDetails={bookingDetails} />
            </div>
          </div>
          <div className="price-block mt-2 bg-light p-2">
            <div className="organiser-block">
              <h5>{t("organiser")}</h5>
              {bookingDetails?.participants
                ?.filter(p => p.isOrganizer === 1)
                .map((org, idx) => (
                  <div key={`org-${idx}`}>{org.userName}</div>
                ))}
            </div>

            {bookingDetails?.participants?.length > 1 && (
              <div className="guest-block mt-3">
                <h5>{t("guests")}</h5>
                {bookingDetails?.participants
                  ?.filter(p => p.isOrganizer === 0)
                  .map((guest, idx) => (
                    <div key={`guest-${idx}`}>{guest.userName}</div>
                  ))}
              </div>
            )}
          </div>
        </>
        <>
          <hr className="mt-3 mb-1" />
          {isShowWarningMsg && (
            <div className="mt-4">
              <WarningMessageBox message={bookingDetails?.reason} />
            </div>
          )}
          {isShowCancelReason && (
            <div className="mt-4">
              <WarningMessageBox
                message={`${t("cancelBookingUntil")} ${moment(
                  bookingDetails?.cancellationUntil?.date
                ).format(CONSTANTS.DATE_FORMATS.DAY_MONTH_YEAR_FORMAT)}`}
              />
            </div>
          )}

          {isShowDeleteButton && (
            <button
              className="btn fg-btn fg-btn-grey mt-0"
              onClick={() => toggleCancelModal(true)}
              disabled={!bookingDetails?.bookingCancelable}
            >
              {t("cancelBooking")}
            </button>
          )}
        </>
      </ModalBody>
    </Modal>
  );
};

export default ReservationDetails;
